import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Filter, CheckCircle, AlertCircle, Clock, Calendar, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAllTasks } from '../lib/api';
import { TranscriptionResponse } from '../lib/types';
import { ProcessingCard } from '../components/transcription/ProcessingCard';
import { TranscriptionCard } from '../components/transcription/TranscriptionCard';
import { ErrorMessage } from '../components/transcription/ErrorMessage';
export function Transcriptions() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [transcriptions, setTranscriptions] = useState<TranscriptionResponse[]>([]);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetchTranscriptions();
  }, []);
  const fetchTranscriptions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAllTasks();
      const sortedTasks = response.tasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setTranscriptions(sortedTasks);
    } catch (err) {
      console.error('Error fetching transcriptions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transcriptions');
    } finally {
      setIsLoading(false);
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-green-500" data-id="element-2215" />;
      case 'processing':
      case 'pending':
        return <Clock size={16} className="text-blue-500" data-id="element-2216" />;
      case 'failed':
        return <AlertCircle size={16} className="text-red-500" data-id="element-2217" />;
      default:
        return null;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return `Today, ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };
  const filteredTranscriptions = transcriptions.filter(item => {
    if (searchTerm !== '' && !item.title?.toLowerCase().includes(searchTerm.toLowerCase()) && !item.video_id?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterStatus && item.status !== filterStatus) {
      return false;
    }
    return true;
  });
  return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2218">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6" data-id="element-2219">
        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold" data-id="element-2220">
          Transcriptions
        </h1>
        <Link to="/app" data-id="element-2221">
          <Button className="flex items-center gap-2 w-full sm:w-auto" data-id="element-2222">
            New Transcription
          </Button>
        </Link>
      </div>
      <Card className="mb-6" data-id="element-2223">
        <CardContent className="p-3 md:p-4" data-id="element-2224">
          <div className="flex flex-col md:flex-row gap-4 justify-between" data-id="element-2225">
            <div className="relative flex-1" data-id="element-2226">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-2227" />
              <Input className="pl-10" placeholder="Search transcriptions" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2228" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-id="element-2229">
              <Button variant={filterStatus === null ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus(null)} className="whitespace-nowrap text-xs h-8 px-2" data-id="element-2230">
                All
              </Button>
              <Button variant={filterStatus === 'completed' ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus('completed')} className="whitespace-nowrap text-xs h-8 px-2" data-id="element-2231">
                <CheckCircle size={12} className="mr-1" data-id="element-2232" />
                Completed
              </Button>
              <Button variant={filterStatus === 'processing' ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus('processing')} className="whitespace-nowrap text-xs h-8 px-2" data-id="element-2233">
                <Clock size={12} className="mr-1" data-id="element-2234" />
                Processing
              </Button>
              <Button variant={filterStatus === 'failed' ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus('failed')} className="whitespace-nowrap text-xs h-8 px-2" data-id="element-2235">
                <AlertCircle size={12} className="mr-1" data-id="element-2236" />
                Failed
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      {isLoading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-id="element-2237">
          {[...Array(4)].map((_, i) => <Card key={i} className="h-full" data-id="element-2238">
              <div className="h-40 bg-secondary/40 animate-pulse rounded-t-lg" data-id="element-2239"></div>
              <CardContent className="p-4" data-id="element-2240">
                <div className="h-4 bg-secondary/40 animate-pulse rounded mb-2" data-id="element-2241"></div>
                <div className="h-4 bg-secondary/40 animate-pulse rounded w-3/4" data-id="element-2242"></div>
              </CardContent>
            </Card>)}
        </div> : error ? <ErrorMessage message={error} onRetry={fetchTranscriptions} data-id="element-2243" /> : filteredTranscriptions.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-2244">
          {filteredTranscriptions.map(item => item.status === 'processing' || item.status === 'pending' ? <ProcessingCard key={item.task_id} startTime={new Date(item.created_at).getTime()} videoId={item.video_id || undefined} url={item.video_id} taskId={item.task_id} data-id="element-2245" /> : <TranscriptionCard key={item.task_id} transcription={item} data-id="element-2246" />)}
        </div> : <Card data-id="element-2247">
          <CardContent className="p-6 md:p-12 text-center" data-id="element-2248">
            <div className="text-gray-400 mb-4" data-id="element-2249">
              <Search size={36} className="mx-auto" data-id="element-2250" />
            </div>
            <h3 className="text-lg font-medium mb-1" data-id="element-2251">No results found</h3>
            <p className="text-sm text-gray-500" data-id="element-2252">
              Try adjusting your search or create a new transcription
            </p>
            <Link to="/app" data-id="element-2253">
              <Button className="mt-4 w-full sm:w-auto" data-id="element-2254">
                New Transcription
              </Button>
            </Link>
          </CardContent>
        </Card>}
    </div>;
}